import AdminHooks from "../admin/hooks.js";
import VesselLocation from "../views/crewing/vessel_location";
import Sortable from "sortablejs";
import topbar from "topbar";
import { registerEvent } from "../components/plausible";

topbar.config({ barColors: { 0: "#29d" }, shadowColor: "rgba(0, 0, 0, .3)" });

declare var window: any;

interface IFlashMessage {
  type: string;
  message: string;
  title: string;
}

const Hooks = {
  Accordion: AdminHooks.Accordion,
  ApplyButtonState: {},
  AutoScrollLock: {},
  SimpleFormSubmit: {},
  Confirm: {},
  DatePicker: AdminHooks.DatePicker,
  PlausibleSubmit: {},
  DisableListNotification: {},
  Dropdown: AdminHooks.Dropdown,
  ExpenseForm: {},
  Filter: AdminHooks.Filter,
  HandleCopyToClipBoard: {},
  HandleFilterDropdown: {},
  HandleFormLoading: {},
  HandlePageLoading: {},
  HandleDragNDrop: {},
  HandleUpdateRecordsPosition: {},
  InitFeather: {},
  InvoiceForm: {},
  PushEvent: {},
  RemoveNotification: {},
  Search: AdminHooks.Search,
  ScrollBottom: {},
  ScrollLock: {},
  ScrollTop: {},
  Select: AdminHooks.Select,
  Tooltip: AdminHooks.Tooltip,
  UpdateAlert: {},
  UpdateNotificationBadge: {},
  EditorJS: AdminHooks.EditorJS,
  VesselLocation: VesselLocation,
};

const updateFlash = (props: IFlashMessage) => {
  const { type, message, title } = props;

  window.dispatchEvent(
    new CustomEvent("flash", {
      detail: { type: type, message: message, title: title },
    }),
  );
};

// Identify which button was clicked on form submission.
Hooks.ApplyButtonState = {
  mounted(this: any) {
    const target = this.el.dataset.target || "tracking-submit-input";
    const hiddenInput = <HTMLInputElement>document.querySelector(`#${target}`);
    this.el.addEventListener("mouseenter", (e: any) => {
      if (hiddenInput) hiddenInput.value = e.target.getAttribute("name");
    });
  },
};

// Place this hook on the modal's root element to automatically
// lock the scroll when the modal is opened and unlock if it is
// closed
Hooks.AutoScrollLock = {
  lockScroll(lock: boolean) {
    let event = new CustomEvent("scroll-lock", { detail: lock });
    window.dispatchEvent(event);
  },
  mounted(this: any) {
    this.lockScroll(true);
  },
  destroyed(this: any) {
    this.lockScroll(false);
  },
  disconnected(this: any) {
    this.lockScroll(false);
  },
};

Hooks.PlausibleSubmit = {
  mounted(this: any) {
    this.el.addEventListener(
      "submit",
      (event: any) => {
        // Prevent LiveView's default immediate submission
        event.preventDefault();

        // Get analytics label from the submit button
        const button = this.el.querySelector(
          "button[type=submit][data-liveview-analytics]",
        );
        const analyticsLabel = button?.getAttribute("data-liveview-analytics");

        if (analyticsLabel) {
          registerEvent(analyticsLabel);
        }

        setTimeout(() => {
          // Trigger the form submission after a short delay
          this.el.dispatchEvent(
            new Event("submit", { bubbles: true, cancelable: true }),
          );
        }, 150);
      },
      { once: true },
    );
  },
};

/*
Place this hook to pop up a confirmation dialog when an element is clicked
A `phx-confirm` attribute is required to set the message to be displayed.

To conditionally prevent the popup, you may add a `phx-no-confirm` attribute to the element.

Examples:
```
<button
  phx-click="do_something"
  phx-hook="Confirm"
  phx-confirm="Are you sure you want to do something?">
  Do something
</button>

<button
  phx-click="maybe_confirm"
  phx-hook="Confirm"
  phx-confirm="Are you sure you want to do something?"
  phx-no-confirm={some_boolean}>
  Maybe confirm
</button>
```
*/
Hooks.Confirm = {
  openDialog(this: any, message: string) {
    const modal = document.getElementById("confirmation-popup");
    if (modal) {
      modal.querySelector("[data-popup-message]")!.textContent = message;
      window.liveSocket.execJS(modal, modal.getAttribute("data-exec-open"));
    }
  },
  mounted(this: any) {
    const element = this.el;
    const message = element.getAttribute("phx-confirm");
    if (!message) {
      throw new Error("Attribute phx-confirm is not set");
    }
    element.addEventListener("click", (e: any) => {
      const preventConfirm = e.target.hasAttribute("phx-no-confirm");
      if (preventConfirm) return;
      if (e.isTrusted) {
        e.stopPropagation();
        e.preventDefault(); // To prevent submits for submit buttons
        const callback = () => {
          e.target.dispatchEvent(new PointerEvent("click", { bubbles: true }));
        };
        this.openDialog(message);
        window.addEventListener("phx:confirm", callback, { once: true });
        window.addEventListener("phx:cancel", () => {
          window.removeEventListener("phx:confirm", callback);
        });
      }
    });
  },
};

Hooks.DisableListNotification = {
  mounted(this: any) {
    const menu = document.querySelector(".dropdown");

    document.addEventListener("mouseup", (e: MouseEvent) => {
      if (menu && !(menu as HTMLElement).contains(e.target as Node)) {
        this.pushEvent("disable_list_notification", { is_active: false });
      }
    });
  },
};

Hooks.ExpenseForm = {
  setSelectListener(this: any, selector: string) {
    const select = document.querySelector(selector);
    select?.addEventListener("change", () => {
      select.dispatchEvent(new Event("input", { bubbles: true }));
    });
  },
  mounted(this: any) {
    this.setSelectListener(
      "select[id^='invoice-form_expenses'][id$='_currency_code']",
    );
    this.setSelectListener(
      "select[id^='invoice-form_expenses'][id$='_product_type_id']",
    );
    this.handleEvent("remove_seafarer", ({ id, target }: any) => {
      this.pushEventTo(target, "remove_seafarer", { id: id });
    });
  },
};

// Reference: https://hexdocs.pm/phoenix_live_view/js-interop.html#live-navigation-events
Hooks.HandlePageLoading = {
  mounted(this: any) {
    window.addEventListener("phx:page-loading-start", () => topbar.show());
    window.addEventListener("phx:page-loading-stop", (e: CustomEvent) => {
      topbar.hide();
      if (e.detail.kind === "patch") window.scrollTo({ top: 0 });
    });
  },
};

Hooks.HandleDragNDrop = {
  mounted(this: any) {
    const dragNDropList = document.querySelector(
      '[data-sort="drag-n-drop-list"]',
    ) as HTMLElement;
    !!dragNDropList &&
      Sortable.create(dragNDropList, {
        handle: ".sort-menu",
      });
  },
  updated(this: any) {
    const dragNDropList = document.querySelector(
      '[data-sort="drag-n-drop-list"]',
    ) as HTMLElement;
    !!dragNDropList &&
      Sortable.create(dragNDropList, {
        handle: ".sort-menu",
      });
  },
};

Hooks.HandleUpdateRecordsPosition = {
  mounted(this: any) {
    this.el.addEventListener("click", (_e: any) => {
      var elements = document.querySelectorAll("[data-order-id]");
      var orderIds = Array.from(elements).map(function (element) {
        return element.getAttribute("data-order-id");
      });
      !!elements &&
        this.pushEvent("update_records_position", {
          data: orderIds,
        });
    });
  },
};

Hooks.HandleFilterDropdown = {
  mounted(this: any) {
    const toggleFilterDropdown = ({ open }: any) => {
      var event = new CustomEvent("toggle-filter-dropdown", { detail: open });
      window.dispatchEvent(event);
    };
    this.handleEvent("toggle_filter_dropdown", toggleFilterDropdown);
  },
};

Hooks.HandleFormLoading = {
  loadingListener(this: any) {
    const submitBtn = this.el.querySelector("button[type='submit']");
    if (submitBtn) {
      const currentLabel = submitBtn.text;
      const loadingLabel =
        submitBtn.getAttribute("phx-disable-with") || "Saving...";
      this.handleEvent("start_loading", () => {
        submitBtn.disabled = true;
        submitBtn.innerHTML = loadingLabel;
      });

      this.handleEvent("stop_loading", () => {
        submitBtn.disabled = false;
        submitBtn.innerHTML = currentLabel;
      });
    }
  },
  mounted(this: any) {
    this.loadingListener();
  },
  updated(this: any) {
    this.loadingListener();
  },
};

/*
Use for copying text to clipboard by a click of a button or link

You may use `data-copy-target` attribute to specify the target element to copy the text from.
Example:
  <textarea id={"text-container"} rows="9" disabled class="hidden">
    Text to copy
  </textarea>

  <button
    phx-hook="HandleCopyToClipBoard"
    id="some-id"
    data-copy-target={"text-container"}
    ...
  >
    Copy to clipboard
  </button>

Or use a class text-widget if you don't want to specify the target element.
Example:
  <textarea class="textarea-widget" rows="9" disabled class="hidden">
    Text to copy
  </textarea>

  <button
    phx-hook="HandleCopyToClipBoard"
    id="some-id"
    ...
  >
    Copy to clipboard
  </button>
*/
Hooks.HandleCopyToClipBoard = {
  copyTextNavigator(element: any) {
    const value = element.value;
    navigator.clipboard.writeText(value).then(
      () => {
        updateFlash({
          type: "success",
          message: "Copied to clipboard",
          title: "Success",
        });
      },
      (_err) => {
        updateFlash({
          type: "error",
          message: "Could not be copied to clipboard",
          title: "Sorry",
        });
      },
    );
  },
  copyTextDocument(element: any) {
    try {
      element.disabled = false;
      element.select();
      if (document.execCommand("copy")) {
        updateFlash({
          type: "success",
          message: "Copied to clipboard",
          title: "Success",
        });
      } else {
        updateFlash({
          type: "error",
          message: "Could not be copied to clipboard",
          title: "Sorry",
        });
      }
      element.disabled = true;
    } catch (err) {
      updateFlash({
        type: "error",
        message: "Could not be copied to clipboard",
        title: "Sorry",
      });
    }
  },
  copyTextFromElement(this: any, element: any) {
    if (!navigator.clipboard) {
      this.copyTextDocument(element);
    } else {
      this.copyTextNavigator(element);
    }
  },
  mounted(this: any) {
    this.el.addEventListener("click", (e: any) => {
      // stop propagation and prevent default so copy button can be placed inside links
      e.stopPropagation();
      e.preventDefault();

      const copyTarget = this.el.getAttribute("data-copy-target");
      let widgetTextarea = copyTarget
        ? document.getElementById(copyTarget)
        : document.querySelector(".textarea-widget");

      this.copyTextFromElement(widgetTextarea);
    });

    this.handleEvent("copy_to_clipboard", (data: any) => {
      let widgetTextarea = document.querySelector(data.copy_target);
      this.copyTextFromElement(widgetTextarea);
    });
  },
};

Hooks.InitFeather = {
  initFeather() {
    const feather = require("feather-icons");
    feather.replace();
  },
  mounted(this: any) {
    this.initFeather();
  },
  updated(this: any) {
    this.initFeather();
  },
};

Hooks.InvoiceForm = {
  currentFormQuery: null,
  setSelectListener(this: any, selector: string) {
    const element = document.querySelector(selector);

    element?.addEventListener("change", () => {
      element.dispatchEvent(new Event("input", { bubbles: true }));
    });
  },
  mounted(this: any) {
    this.setSelectListener("#invoice-form_vessel_id", "vessel_id");
    this.setSelectListener("#invoice-form_currency_code", "currency_code");
    this.setSelectListener("#invoice-form_payment_terms", "payment_terms");
    this.handleEvent("select_option", ({ id, option }: any) => {
      const element = <HTMLInputElement>document.querySelector(`#${id}`);
      if (element) {
        element.value = option;
        element.dispatchEvent(new Event("change"));
      }
    });
    this.handleEvent("verify_form_changes", () => {
      let changed: any = {};
      this.currentFormQuery !== this.getIncomingFormQuery(this)
        ? (changed.value = true)
        : (changed.value = false);
      this.pushEvent("form_changed", changed);
    });
    this.handleEvent("save_current_form_query", () => {
      this.currentFormQuery = this.getIncomingFormQuery(this);
    });
    window.addEventListener("beforeunload", function (e: any) {
      if (document.querySelectorAll("form[phx-form-dirty]").length > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    });
  },
  reconnected(this: any) {
    const vesselId = (<HTMLInputElement>(
      document.querySelector("#invoice-form_vessel_id")
    ))?.value;
    const invoiceDate = (<HTMLInputElement>(
      document.querySelector("#invoice-form_invoice_date")
    ))?.value;
    this.pushEventTo(
      "#confirmations-table",
      "prelist_confirmations_after_reconnect",
      { vessel_id: vesselId, invoice_date: invoiceDate },
    );
  },
  getIncomingFormQuery(this: any) {
    let form = this.el.closest("form");
    let formData: any = new FormData(form);
    formData.delete("invoice[vessel_id]");
    formData.delete("invoice[invoice_date]");
    let queryString = new URLSearchParams(formData).toString();
    return queryString;
  },
};

Hooks.PushEvent = {
  mounted() {
    window.pushEventHook = this;
  },
};

Hooks.RemoveNotification = {
  mounted(this: any) {
    this.el.addEventListener("click", (_e: any) => {
      const notification_id = this.el.getAttribute("phx-value-notification_id");
      window.removeNotification(notification_id);
      this.pushEvent("remove_notification", {
        notification_id: notification_id,
      });
    });
  },
};

// event autoscroll to last message to message box
Hooks.ScrollBottom = {
  mounted(this: any) {
    this.el.scrollTop = this.el.scrollHeight;
  },
  updated(this: any) {
    this.el.scrollTop = this.el.scrollHeight;
  },
};

Hooks.ScrollLock = {
  mounted(this: any) {
    const handleScrollLock = ({ lock }: any) => {
      var event = new CustomEvent("scroll-lock", { detail: lock });
      window.dispatchEvent(event);
    };
    this.handleEvent("toggle_scroll_lock", handleScrollLock);
  },
};

Hooks.ScrollTop = {
  mounted(this: any) {
    window.scrollTo(0, 0);
  },
};

Hooks.UpdateAlert = {
  mounted(this: any) {
    const handleUpdateAlert = ({ type, message }: any) => {
      var event = new CustomEvent("flash", {
        detail: { type: type, message: message },
      });
      window.dispatchEvent(event);
    };
    this.handleEvent("update_alert", handleUpdateAlert);
  },
};

Hooks.SimpleFormSubmit = {
  mounted(this: any) {
    const currentView = this;
    const form = this.el;
    form.addEventListener("submit", (event: any) => {
      event.preventDefault();
      const targetContext = form.getAttribute("phx-target") as string;
      const phxEvent = form.getAttribute("phx-submit") as string;

      currentView.liveSocket.blurActiveElement(form);

      currentView.__view.pushFormSubmit(
        form,
        +targetContext,
        phxEvent,
        undefined,
        {},
        () => {
          currentView.liveSocket.restorePreviouslyActiveFocus();
        },
      );
    });
  },
};

Hooks.UpdateNotificationBadge = {
  updated(this: any) {
    window.getNotificationBadge();
  },
};

export default Hooks;
